/**
 * Vorlesen / TTS-Player — vollständiger Port von public/assets/audio-player.js
 * (SOLL: Paritäts-Doku §5). Zustandsmaschine mit Signals statt DOM-Injection:
 *
 *  - Segmente GET /api/audio/chapter/{m}/{b} (Bearer); fehlende per
 *    POST /api/audio/generate (3 Versuche bei 503); Abspielen fetch→Blob→Audio.
 *  - Gruppierung nach uk_slug = Abschnitte (Lead gemergt; Projekt: „Schritt N").
 *  - Prompts (ask-ki, prompt-hinweis): IMMER Stopp + Gong + Pause bis Klick.
 *    Tabellen-Hinweis: Stopp nur im Kapitel-Modus.
 *  - Abschnittsgrenze: Kapitel-Modus wartet; Durchlesen blättert selbst weiter.
 *  - Kapitelende im Durchlesen: nächstes Kapitel + weiterlesen (continueNext).
 *  - HARTER Stopp bei jeder Nutzer-Navigation; Auto-Vorlesen startet ~700 ms
 *    nach Kapitelwechsel neu. Media Session für Lock-Screen/Kopfhörer.
 *
 * Der Kurs-Screen registriert eine Bridge (Abschnitts-Stand + „Weiter"-Klick),
 * das Pendant zum CourseRuntime-Wrapper des Webs.
 */
import { signal } from "@preact/signals";
import { config } from "../config";
import { getToken } from "../state/session";
import { gong, ensureGongCtx } from "./gong";

const K_AUTO = "ki-kurs-audio-auto";
const K_MODUS = "ki-kurs-audio-modus"; // "kapitel" | "durch"
const K_AUDIO_ON = "ki-kurs-audio-an"; // Hauptschalter Audio An/Aus (13.06.)
const K_SPEED = "ki-kurs-audio-speed"; // Abspieltempo via playbackRate (Web-Parität, Default 0.85)

export interface TtsSegment {
  segment_id: string;
  content_hash?: string;
  kind?: string;
  uk_slug?: string;
  speakable_text?: string;
  audio_url?: string;
}

/** Verbindung zum Kurs-Screen (Web: CourseRuntime + [data-next]). */
export interface TtsBridge {
  moduleId: string;
  blockId: string;
  istProjekt: boolean;
  sectionCount: number;
  getSubIndex: () => number;
  hasNext: () => boolean;
  /** Programmatischer „Weiter"-Klick (Abschnitt gelesen + weiter / nächstes Kapitel). */
  klickWeiter: () => void;
  /** Weicher Abschnittswechsel OHNE Vorlesen zu stoppen (für Überspringen über Grenze). */
  goToSection?: (idx: number) => void;
  chapterTitle: string;
}

/* ---------- öffentliche UI-Signale ---------- */
export const ttsPlaying = signal(false);
export const ttsWaiting = signal<null | "prompt" | "weiter">(null);
export const ttsLoading = signal(false);
export const ttsModus = signal<"kapitel" | "durch">(
  localStorage.getItem(K_MODUS) === "durch" ? "durch" : "kapitel",
);
export const ttsAuto = signal(localStorage.getItem(K_AUTO) === "1");

/** Hauptschalter Audio An/Aus (Auftraggeber 13.06.): nur wenn AN sind die Tipp-
 *  Gesten auf Text/Knopf aktiv. AUS = wie Stopp + alle Audio-Interaktionen tot. */
export const ttsAudioOn = signal(localStorage.getItem(K_AUDIO_ON) === "1");

/** Pausiert (Position gemerkt, reines Play setzt fort)? — getrennt von „läuft". */
export const ttsPaused = signal(false);

export function setTtsModus(m: "kapitel" | "durch"): void {
  ttsModus.value = m;
  localStorage.setItem(K_MODUS, m);
}
export function toggleTtsAuto(): void {
  ttsAuto.value = !ttsAuto.value;
  localStorage.setItem(K_AUTO, ttsAuto.value ? "1" : "0");
}

/**
 * Abspieltempo (playbackRate) — Web-Parität zu audio-player.js.
 * Default 1.0 = NEUTRAL (kein Tempo-/Tonhöhen-Eingriff). Feinjustierung über den
 * Regler: Skala -2 … 0 … +2 (Schritt 0,5), 0 = normal, Tempo = 2^(pos/2)
 * (-2 => 0,5x, 0 => 1,0x, +2 => 2,0x). Tempo läuft im BROWSER, NICHT in die TTS
 * gebacken — sonst würden alle content_hashes ungültig.
 * WICHTIG: Tempo ändern darf NICHT die Tonhöhe ändern → preservesPitch (und es
 * muss VOR playbackRate gesetzt werden, sonst greift es in mobilen WebViews nicht).
 */
export const SPEED_MIN_POS = -2;
export const SPEED_MAX_POS = 2;
export const SPEED_STEP = 0.5;
export function rateFromPos(pos: number): number {
  return Math.pow(2, pos / 2);
}
export function posFromRate(rate: number): number {
  let p = 2 * (Math.log(rate) / Math.LN2);
  p = Math.round(p / SPEED_STEP) * SPEED_STEP;
  return Math.max(SPEED_MIN_POS, Math.min(SPEED_MAX_POS, p));
}
function speed(): number {
  const v = parseFloat(localStorage.getItem(K_SPEED) || "1.0");
  return Number.isFinite(v) && v >= 0.5 && v <= 3 ? v : 1.0;
}
/** Reglerposition (-2..+2) als Signal für die UI. */
export const ttsSpeedPos = signal<number>(posFromRate(speed()));
/** Tempo setzen (rate) + live auf laufende Wiedergabe anwenden, Tonhöhe halten. */
export function setTtsSpeedPos(pos: number): void {
  const rate = rateFromPos(pos);
  ttsSpeedPos.value = pos;
  localStorage.setItem(K_SPEED, String(rate));
  if (S.audio) {
    try {
      const am = S.audio as unknown as {
        preservesPitch?: boolean;
        mozPreservesPitch?: boolean;
        webkitPreservesPitch?: boolean;
        playbackRate: number;
      };
      am.preservesPitch = true;
      am.mozPreservesPitch = true;
      am.webkitPreservesPitch = true;
      am.playbackRate = rate;
    } catch {
      /* ignore */
    }
  }
}

/** Audio-Hauptschalter umlegen. Beim Ausschalten hart stoppen (Reset auf
 *  Abschnittsanfang); beim Einschalten passiert nichts von selbst (kein Auto-Play). */
export function toggleTtsAudioOn(): void {
  ttsAudioOn.value = !ttsAudioOn.value;
  localStorage.setItem(K_AUDIO_ON, ttsAudioOn.value ? "1" : "0");
  if (!ttsAudioOn.value) { S.playMode = false; stoppHart(); }
}

/* ---------- interner Zustand (wie S im Web) ---------- */
const S = {
  chapterKey: "",
  groups: [] as TtsSegment[][],
  flat: [] as TtsSegment[],
  aligned: false,
  gi: 0,
  si: 0,
  continueNext: false,
  playMode: false, // GEMERKTER Play-Modus (Web-Parität F12): bleibt an bis ■/Prompt;
                   // entscheidet, ob Navigation automatisch weiterliest (auch nach
                   // automatischem Grenz-/Kapitelende-Stopp). Auftraggeber 2026-06-18.
  selfNav: false,
  audio: null as HTMLAudioElement | null,
  blobUrl: null as string | null,
  runId: 0,
};

/** Merkposten beim Stoppen: exakt dort weiterlesen, wenn man wieder Play drückt
 *  (Auftraggeber 12.06., Task D) — Sekunden-genau im Segment. */
let resumeAt: { chapterKey: string; gi: number; si: number; time: number } | null = null;

export function clearResume(): void {
  resumeAt = null;
}

let bridge: TtsBridge | null = null;
export function setTtsBridge(b: TtsBridge | null): void {
  bridge = b;
}

/** Ohne Kapitel (Modul-Eröffnungsseite): ▶ navigiert ins erste Kapitel und liest
 *  dort los — der Kurs-Screen registriert die Navigation als Fallback. */
let playFallback: (() => void) | null = null;
let forceResume = false;
export function setTtsPlayFallback(fn: (() => void) | null): void {
  playFallback = fn;
}
/** Nach der NÄCHSTEN Navigation weiterlesen (Tipp auf Kapitel, während Play an ist). */
export function resumeAfterNavigate(): void {
  forceResume = true;
}
export function ttsSelfNav(): boolean {
  return S.selfNav;
}

const STOP_KINDS_IMMER: Record<string, 1> = { "ask-ki": 1, "prompt-hinweis": 1 };

/* ---------- API (Bearer wie completion.ts; Audio-URLs sind API-Pfade) ---------- */
function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const h: Record<string, string> = { ...(extra || {}) };
  const t = getToken();
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}
function apiUrl(path: string): string {
  if (/^https?:/i.test(path)) return path;
  return `${config.apiBaseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
}

async function ladeSegmente(mId: string, bId: string): Promise<TtsSegment[]> {
  const res = await fetch(apiUrl(`/api/audio/chapter/${encodeURIComponent(mId)}/${encodeURIComponent(bId)}`), {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return ((await res.json()) as { segments?: TtsSegment[] }).segments || [];
}

const warte = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function erzeugeSegment(seg: TtsSegment): Promise<string> {
  for (let v = 0; v < 3; v++) {
    const res = await fetch(apiUrl("/api/audio/generate"), {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ segment_id: seg.segment_id, content_hash: seg.content_hash }),
    });
    if (res.status === 503) {
      await warte(3000);
      continue;
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return ((await res.json()) as { audio_url: string }).audio_url;
  }
  throw new Error("Timeout");
}

/** Audio-URL eines Segments holen — dedupliziert parallele Anfragen (Pre-Fetch
 *  vs. Abspielen), damit kein Segment doppelt erzeugt wird. */
function holeAudioUrl(seg: TtsSegment): Promise<string> {
  if (seg.audio_url) return Promise.resolve(seg.audio_url);
  const s = seg as TtsSegment & { _gen?: Promise<string> };
  if (!s._gen) {
    s._gen = erzeugeSegment(seg)
      .then((u) => {
        seg.audio_url = u;
        s._gen = undefined;
        return u;
      })
      .catch((e) => {
        s._gen = undefined;
        throw e;
      });
  }
  return s._gen;
}

function blobWeg(): void {
  if (S.blobUrl) {
    URL.revokeObjectURL(S.blobUrl);
    S.blobUrl = null;
  }
}

async function spieleUrl(url: string, startAt = 0): Promise<void> {
  const res = await fetch(apiUrl(url), { headers: authHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  blobWeg();
  S.blobUrl = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const a = new Audio(S.blobUrl!);
    // Tempo im Browser steuern (Web-Parität): preservesPitch ZUERST, dann playbackRate.
    // Sonst halten manche mobile WebViews die Tonhöhe NICHT und die Stimme klingt beim
    // Bremsen tiefer (= Ursache des "in der App tiefer"-Effekts). Tempo ≠ Tonhöhe.
    try {
      const am = a as unknown as {
        preservesPitch?: boolean;
        mozPreservesPitch?: boolean;
        webkitPreservesPitch?: boolean;
      };
      am.preservesPitch = true;
      am.mozPreservesPitch = true;
      am.webkitPreservesPitch = true;
    } catch {
      /* ignore */
    }
    a.playbackRate = speed();
    S.audio = a;
    // Resume mitten im Segment (Task D): erst springen, wenn die Dauer bekannt ist.
    if (startAt > 0.25) {
      a.addEventListener(
        "loadedmetadata",
        () => {
          try {
            if (!Number.isFinite(a.duration) || startAt < a.duration - 0.5) a.currentTime = startAt;
          } catch {
            /* ignore */
          }
        },
        { once: true },
      );
    }
    a.onended = () => resolve();
    a.onerror = () => reject(new Error("Abspielfehler"));
    a.play().catch(reject);
  });
}

/* ---------- Segmente → Abschnitts-Gruppen (1:1 zu gruppiere()) ---------- */
function gruppiere(segs: TtsSegment[], istProjekt: boolean): TtsSegment[][] {
  if (!segs.length) return [];
  const groups: TtsSegment[][] = [];
  let cur: TtsSegment[] = [];
  let lastSlug = segs[0].uk_slug || "";
  segs.forEach((s) => {
    let grenze: boolean;
    if (istProjekt) {
      grenze = s.kind === "heading" && /^Schritt \d/.test(s.speakable_text || "");
    } else {
      const slug = s.uk_slug || "";
      grenze = slug !== lastSlug;
      lastSlug = slug;
    }
    if (grenze && cur.length) {
      groups.push(cur);
      cur = [];
    }
    cur.push(s);
  });
  if (cur.length) groups.push(cur);
  if (!istProjekt && groups.length > 1 && !(groups[0][0].uk_slug || "")) {
    groups[1] = groups[0].concat(groups[1]);
    groups.shift();
  }
  return groups;
}

/* ---------- Media Session (Lock-Screen/Kopfhörer) ---------- */
function mediaSession(): void {
  if (!("mediaSession" in navigator)) return;
  try {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: bridge?.chapterTitle || "KI-Kurs",
      artist: "Sentinus KI-Kurs",
    });
    navigator.mediaSession.setActionHandler("play", () => {
      if (ttsWaiting.value) fortsetzen();
      else void spieleAb();
    });
    navigator.mediaSession.setActionHandler("pause", () => pause());
    navigator.mediaSession.setActionHandler("stop", () => {
      S.playMode = false; // Stopp vom Sperrbildschirm = Stopp-Modus → kein Auto
      stoppHart();
    });
  } catch {
    /* optional */
  }
}

/* ---------- Wiedergabe ---------- */
async function ladeKapitel(): Promise<boolean> {
  if (!bridge || !getToken()) return false;
  const key = `${bridge.moduleId}/${bridge.blockId}`;
  if (S.chapterKey === key && S.flat.length) return true;
  S.chapterKey = key;
  try {
    S.flat = await ladeSegmente(bridge.moduleId, bridge.blockId);
  } catch {
    S.flat = [];
  }
  const istProjekt = bridge.istProjekt || S.flat.some((s) => /^Schritt \d/.test(s.speakable_text || ""));
  S.groups = gruppiere(S.flat, istProjekt);
  const anz = bridge.sectionCount;
  // aligned, sobald Abschnitte und Audio-Gruppen 1:1 passen — auch bei
  // Einzelabschnitt-Kapiteln (anz===1). Nur bei echtem Mismatch (z. B. m08-Projekte)
  // Fallback aufs ganze Kapitel. Web-Parität: audio-player.js (anz >= 1).
  S.aligned = anz >= 1 && S.groups.length === anz;
  if (!S.aligned) S.groups = [S.flat]; // Fallback: eine Gruppe = ganzes Kapitel
  S.gi = 0;
  S.si = 0;
  return S.flat.length > 0;
}

function aktuelleGruppe(): number {
  if (S.aligned && bridge) {
    return Math.max(0, Math.min(S.groups.length - 1, bridge.getSubIndex()));
  }
  return S.gi;
}

export async function spieleAb(keepPos = false, startGroup?: number): Promise<void> {
  // Kein Kapitel offen (Modulseite) → erst hinnavigieren, dann automatisch lesen.
  if (!bridge && playFallback) {
    forceResume = true;
    playFallback();
    return;
  }
  const lauf = ++S.runId;
  haltAudioAn();
  ttsLoading.value = true;
  const ok = await ladeKapitel();
  ttsLoading.value = false;
  if (!ok || S.runId !== lauf) return;
  ttsPlaying.value = true;
  S.playMode = true; // Vorlesen läuft = gemerkter Play-Modus an
  ttsPaused.value = false;
  ttsWaiting.value = null;
  // startGroup: expliziter Ziel-Abschnitt (Abschnitts-Klick) — NACH dem Laden
  // angewandt, unabhängig vom evtl. noch alten bridge.getSubIndex() (kein Race).
  if (typeof startGroup === "number" && S.aligned) {
    S.gi = Math.max(0, Math.min(S.groups.length - 1, startGroup));
    S.si = 0;
  } else if (!keepPos) {
    // Sonst IMMER am ANGEZEIGTEN Abschnitt beginnen (13.06. Punkt 1): Gruppe aus der
    // UI-Position, Segment auf 0 — nie ein altes S.si mitten in den Abschnitt tragen.
    S.gi = aktuelleGruppe();
    S.si = 0;
  }
  // Resume (Task D): Wurde zuvor PAUSIERT und seitdem NICHT in einen anderen
  // Abschnitt navigiert, liest reines Play GENAU an der gemerkten Stelle weiter.
  let startAt = 0;
  if (!keepPos && resumeAt && resumeAt.chapterKey === S.chapterKey) {
    const g = S.groups[resumeAt.gi];
    const gueltig = !!g && resumeAt.si < g.length; // Gruppen können sich geändert haben
    if (gueltig && (!S.aligned || resumeAt.gi === S.gi)) {
      S.gi = resumeAt.gi;
      S.si = resumeAt.si;
      startAt = resumeAt.time;
    }
  }
  resumeAt = null; // einmalig verwenden
  mediaSession();

  while (ttsPlaying.value && S.runId === lauf) {
    const grp = S.groups[S.gi];
    if (!grp) break;

    while (ttsPlaying.value && S.runId === lauf && S.si < grp.length) {
      const seg = grp[S.si];
      try {
        ttsLoading.value = true;
        const url = await holeAudioUrl(seg);
        if (S.runId !== lauf || !ttsPlaying.value) return;
        ttsLoading.value = false;
        // Pre-Fetch (Task C): das NÄCHSTE Segment schon erzeugen lassen, während
        // dieses läuft — versteckt die Generierungs-Wartezeit an Abschnittsgrenzen.
        const nextSeg = grp[S.si + 1] || S.groups[S.gi + 1]?.[0];
        if (nextSeg && !nextSeg.audio_url) {
          void holeAudioUrl(nextSeg).catch(() => {
            /* wird beim Abspielen erneut versucht */
          });
        }
        await spieleUrl(url, startAt);
        startAt = 0; // nur fürs erste Segment des Resumes
        if (S.runId !== lauf) return;
      } catch (e) {
        ttsLoading.value = false;
        if (S.runId !== lauf) return;
        console.warn("[audio] übersprungen:", seg.segment_id, (e as Error).message);
      }
      S.si++;

      // Prompt-Stopp: IMMER (beide Modi) — Satz wurde gesprochen, jetzt Gong.
      // Verlangt eine Aktion → Play-Modus beenden: danach KEIN Auto-Weiterlesen.
      if (seg.kind && STOP_KINDS_IMMER[seg.kind]) {
        ttsPlaying.value = false;
        S.playMode = false;
        ttsWaiting.value = "prompt";
        gong();
        return;
      }
      // Tabellen-Hinweis: nur im Kapitel-Modus stoppen (auch hier Play-Modus beenden).
      if (seg.kind === "hinweis-tabelle" && ttsModus.value === "kapitel") {
        ttsPlaying.value = false;
        S.playMode = false;
        ttsWaiting.value = "prompt";
        gong();
        return;
      }
    }
    if (!ttsPlaying.value || S.runId !== lauf) return;

    // Gruppe fertig → Abschnittsgrenze (Weiter-Punkt)
    S.si = 0;
    const hatNext = !!bridge?.hasNext();

    if (S.gi + 1 < S.groups.length) {
      if (ttsModus.value === "durch") {
        S.gi++;
        if (S.aligned && hatNext) klickWeiter();
        continue; // nahtlos weiterlesen
      }
      ttsPlaying.value = false;
      ttsWaiting.value = "weiter";
      return;
    }

    // Kapitel zu Ende
    if (ttsModus.value === "durch" && hatNext) {
      S.continueNext = true; // onChapterChange liest im nächsten Kapitel weiter
      klickWeiter();
      ttsPlaying.value = false;
      return;
    }
    stopp();
    return;
  }
}

function klickWeiter(): void {
  if (!bridge) return;
  S.selfNav = true;
  try {
    bridge.klickWeiter();
  } finally {
    setTimeout(() => {
      S.selfNav = false;
    }, 50);
  }
}

export function fortsetzen(): void {
  S.playMode = true; // ▶ nach Gong/Grenze = wieder im Play-Modus
  if (ttsWaiting.value === "weiter") {
    // Nutzerfreundlich: die Leiste drückt das Kurs-„Weiter" gleich mit.
    if (S.aligned && bridge?.hasNext()) {
      ttsWaiting.value = null;
      S.gi++;
      klickWeiter();
      setTimeout(() => void spieleAb(true), 450);
      return;
    }
    S.gi = Math.min(S.gi + 1, S.groups.length - 1);
  }
  ttsWaiting.value = null;
  void spieleAb(true);
}

/** Aktuell läuft/wartet Vorlesen? (für „ab hier weiterlesen" beim Punkt-Klick). */
export function ttsActive(): boolean {
  return ttsPlaying.value || !!ttsWaiting.value;
}

/**
 * Überspringen (⏭): den GERADE vorgelesenen Teil (Segment) auslassen und beim
 * nächsten weiterlesen — z. B. ein vorgelesenes Inhaltsverzeichnis. An der
 * Abschnittsgrenze rückt es weich in den nächsten Abschnitt (UI zieht mit).
 */
export function skipSegment(): void {
  resumeAt = null; // bewusster Sprung → kein altes Resume mehr
  // Pausiert (Gong/Abschnittsende)? → einfach fortsetzen (rückt selbst weiter).
  if (ttsWaiting.value) {
    fortsetzen();
    return;
  }
  if (!ttsPlaying.value) return;
  const grp = S.groups[S.gi];
  if (!grp) return;
  if (S.si < grp.length - 1) {
    // Nächstes Segment im selben Abschnitt — UI bleibt, nur weiter vorlesen.
    S.si += 1;
    void spieleAb(true);
  } else if (S.gi + 1 < S.groups.length) {
    // Abschnittsgrenze: in den nächsten Abschnitt, UI weich mitziehen.
    S.gi += 1;
    S.si = 0;
    if (S.aligned && bridge?.goToSection) {
      S.selfNav = true;
      bridge.goToSection(S.gi);
      setTimeout(() => (S.selfNav = false), 60);
    }
    void spieleAb(true);
  } else {
    // Letztes Segment des letzten Abschnitts → nichts mehr vorzulesen: stoppen.
    stopp();
  }
}

/**
 * Ab dem angeklickten Abschnitt/Absatz lesen (Auftraggeber 13.06., Einzeltipp).
 * Bewusster Sprung → altes Resume verwerfen und am ANFANG des Ziel-Abschnitts starten.
 */
export function playFromSection(idx: number): void {
  S.playMode = true; // Tipp auf einen Absatz = Vorlesen starten = Play-Modus an
  resumeAt = null; // bewusster Abschnittswechsel → kein altes Resume mehr
  if (!S.groups.length) {
    // Noch nicht geladen → Ziel-Abschnitt explizit durchreichen (kein Race auf den
    // evtl. noch alten bridge.getSubIndex(); spieleAb wendet startGroup nach dem
    // Laden an, sofern aligned).
    void spieleAb(false, idx);
    return;
  }
  // Ohne Audio-Ausrichtung (Audio-Gruppen ≠ UI-Abschnitte, m08/m09) lässt sich der
  // einzelne Absatz nicht zielgenau auf eine Audio-Position abbilden. Statt zu
  // stoppen (Tipp soll Audio AUSLÖSEN, 13.06.) lesen wir das Kapitel ab Anfang —
  // das ist das ehrlich Beste ohne den Backend-Ausrichtungsfix.
  if (!S.aligned) {
    S.gi = 0;
    S.si = 0;
    void spieleAb(true);
    return;
  }
  S.gi = Math.max(0, Math.min(S.groups.length - 1, idx));
  S.si = 0;
  void spieleAb(true);
}

function haltAudioAn(): void {
  if (S.audio) {
    try {
      S.audio.pause();
      S.audio.src = "";
    } catch {
      /* ignore */
    }
    S.audio = null;
  }
  blobWeg();
}

/** Gemeinsamer Anhalt-Kern: Wiedergabe-Schleife entwerten, Audio + Timer beenden,
 *  Signale zurücksetzen. Position (resumeAt/S.si) lässt der Aufrufer unangetastet. */
function haltAn(): void {
  S.runId++; // entwertet ALLE laufenden Wiedergabe-Schleifen
  if (resumeTimer) {
    clearTimeout(resumeTimer); // ausstehenden 700-ms-Resume abbrechen (Kursseite verlassen)
    resumeTimer = null;
  }
  // pendingResume NICHT hier zurücksetzen: onNavigateStart setzt es VOR dem Anhalten
  // für den Durchlese-Modus; onChapterReady konsumiert es danach.
  ttsPlaying.value = false;
  ttsWaiting.value = null;
  ttsLoading.value = false;
  haltAudioAn();
}

/**
 * PAUSE (13.06.): Position bleibt erhalten — reines Play setzt GENAU hier fort.
 * Für: Play-Knopf-Einzeltipp während Play, Doppeltipp auf den Lesetext, MediaSession-Pause.
 */
export function pause(): void {
  // An einer Wartegrenze (Prompt / Abschnitts-„Weiter") ist bereits angehalten —
  // den Wartezustand BELASSEN (nicht über haltAn() löschen), damit das nächste Play
  // korrekt weitermacht (fortsetzen). Nur Audio-Element sicher stoppen.
  if (ttsWaiting.value) {
    ttsPaused.value = true;
    haltAudioAn();
    return;
  }
  if (ttsPlaying.value) {
    const a = S.audio;
    // Stelle merken: Sekunde, wenn schon hörbar gelesen; sonst Abschnitts-/Segmentanfang.
    const time = a && !a.ended && (a.currentTime || 0) > 0.5 ? a.currentTime : 0;
    resumeAt = { chapterKey: S.chapterKey, gi: S.gi, si: S.si, time };
    ttsPaused.value = true;
  }
  haltAn();
}

/**
 * STOPP (13.06.): harter Stopp — zurück auf den ANFANG des aktuellen Abschnitts;
 * Position/Resume verworfen, nächstes Play startet vorn. Für: Doppeltipp auf den
 * Play-Knopf, Audio-Hauptschalter Aus, manuelle Navigation, Seite verlassen.
 */
export function stoppHart(): void {
  resumeAt = null;
  S.si = 0; // Reset auf Abschnittsanfang
  ttsPaused.value = false;
  haltAn();
}

/** Beibehaltener Name für Navigations-/Lifecycle-Aufrufer = harter Stopp.
 *  (Über Kapitel-/Seitenwechsel hinweg gilt die gemerkte Stelle ohnehin nicht.) */
export function stopp(): void {
  stoppHart();
}

/**
 * Play-Knopf-Semantik (Auftraggeber 13.06.):
 * - wartet (Prompt/Abschnittsgrenze) → weiter (fortsetzen)
 * - spielt → PAUSE (Position bleibt)
 * - pausiert/gestoppt → Play (Resume an gemerkter Stelle, sonst Abschnittsanfang)
 * Doppeltipp auf den Knopf wird separat über ttsStoppKnopf() (harter Stopp) bedient.
 */
export function ttsPlayKlick(): void {
  // AudioContext im echten Nutzer-Tipp aufwecken (Web-Parität) — sonst bleibt der
  // Gong an Abschnitts-/Prompt-Grenzen stumm (suspended-Context).
  ensureGongCtx();
  if (ttsWaiting.value) fortsetzen();
  else if (ttsPlaying.value) pause();
  else void spieleAb();
}

/** Doppeltipp auf den Play-Knopf = harter Stopp (Reset auf Abschnittsanfang). */
export function ttsStoppKnopf(): void {
  S.playMode = false; // ■ = Stopp-Modus → kein Auto-Weiterlesen mehr
  stoppHart();
}

/**
 * Kapitelwechsel — ZWEIPHASIG, weil das Kapitel in der App asynchron lädt:
 * onNavigateStart() stoppt sofort; onChapterReady() startet erst dann, wenn
 * Segmente + Bridge zum NEUEN Kapitel stehen.
 *
 * Auftraggeber-Regel (12.06.): JEDE manuelle Navigation stoppt das Vorlesen.
 * Weitergelesen wird NUR, wenn der Player selbst weiterblättert (Durchlese-Modus,
 * S.continueNext) oder die Play-Taste auf der Modulseite ins Kapitel springt
 * (forceResume). KEIN Auto-Resume mehr bei manuellem Weiter/Klick.
 */
let pendingResume = false;
let resumeTimer: ReturnType<typeof setTimeout> | null = null;

export function onNavigateStart(): void {
  // Gemerkter Play-Modus (Web-Parität F12, Auftraggeber 2026-06-18): Bin ich im
  // Play-Modus (nicht pausiert) und wechsle das Kapitel → im neuen Kapitel automatisch
  // weiterlesen. Ein ■-Stopp oder Prompt hat playMode bereits beendet → kein Auto.
  pendingResume = forceResume || S.continueNext || (S.playMode && !ttsPaused.value);
  forceResume = false;
  S.continueNext = false;
  stopp();
  // Manuelle Navigation verwirft den Stelle-merken-Posten (Task D gilt nur für
  // Stopp+Play AN ORT UND STELLE — nicht über Kapitelwechsel hinweg).
  resumeAt = null;
  S.chapterKey = ""; // neues Kapitel → Queue neu laden
}

export function onChapterReady(): void {
  if (resumeTimer) clearTimeout(resumeTimer);
  resumeTimer = null;
  // Resume/Durchlesen ODER Auto-Vorlesen (Schalter an UND Audio-Hauptschalter an):
  // ~700 ms nach Kapitelwechsel von vorn starten (Web-Parität: autoAn()).
  const autostart = pendingResume || (ttsAuto.value && ttsAudioOn.value);
  if (autostart && getToken()) resumeTimer = setTimeout(() => void spieleAb(), 700);
  pendingResume = false;
}

/** Harter Stopp bei Nutzer-Navigation INNERHALB des Kapitels (Punkte/Zurück) —
 *  auch im pausierten Zustand, damit kein stale Resume an der alten Stelle hängt. */
export function onUserNav(): void {
  if (S.selfNav) return;
  if (!(ttsPlaying.value || ttsWaiting.value || ttsPaused.value)) return;
  // Web-Parität F12 (Auftraggeber 2026-06-18): Im GEMERKTEN Play-Modus stoppt
  // manuelles "Weiter"/Punkte NICHT hart, sondern liest im NEUEN Abschnitt automatisch
  // weiter — kurz warten, bis goNext/zeigeUnterkapitel den sub_index gesetzt hat (wie Web).
  // Bei Pause oder Stopp-Modus (playMode=false) → harter Stopp wie bisher.
  // (Kapitelende: goNext ruft danach selectChapter→onNavigateStart, dessen haltAn() diesen
  //  250-ms-Timer abräumt und der 700-ms-Kapitel-Resume übernimmt — kein Doppelstart.)
  if (S.playMode && !ttsPaused.value) {
    haltAn(); // laufende Schleife + alte Timer abbrechen (playMode bleibt erhalten)
    const vonIdx = bridge ? bridge.getSubIndex() : -1;
    resumeTimer = setTimeout(() => {
      resumeTimer = null;
      const zuIdx = bridge ? bridge.getSubIndex() : -1;
      // Nur weiterlesen, wenn der Abschnitt sich WIRKLICH geändert hat. Sonst (z. B.
      // Bestätigungsdialog am Kapitelende = noch kein Wechsel) bleibt es gestoppt;
      // ein bestätigter Kapitelwechsel läuft dann über onNavigateStart/onChapterReady.
      if (S.playMode && bridge && getToken() && zuIdx !== vonIdx) void playFromSection(zuIdx);
    }, 250);
  } else {
    stopp();
  }
}

/* Fremder Player startet (Video/Audio im Kapitel) → wir stoppen. */
document.addEventListener(
  "play",
  (e) => {
    if (S.audio && e.target !== S.audio && (ttsPlaying.value || ttsWaiting.value)) {
      S.playMode = false;
      stopp();
    }
  },
  true,
);

/* Seite wird verlassen/versteckt → Wiedergabe beenden (auch Play-Modus aus). */
window.addEventListener("pagehide", () => {
  S.playMode = false;
  stopp();
});

/* Schmaler, NUR LESENDER Zustands-Spiegel für E2E-Tests (repro-audio-bedienung.mjs).
 * Kein Produktionsverhalten — nur eine Momentaufnahme der UI-Signale + interner Index. */
try {
  Object.defineProperty(window, "__tts", {
    get: () => ({
      audioOn: ttsAudioOn.value,
      playing: ttsPlaying.value,
      paused: ttsPaused.value,
      waiting: ttsWaiting.value,
      loading: ttsLoading.value,
      gi: S.gi,
      si: S.si,
      aligned: S.aligned,
      chapterKey: S.chapterKey,
    }),
    configurable: true,
  });
} catch {
  /* window evtl. nicht beschreibbar */
}
